import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../app/store/authSlice';
import { resetContext } from '../../../app/store/contextSlice';
import { refreshOperationBlockStatus } from '../../../app/store/operationBlockSlice';

const formatWindow = (startsAt, endsAt) => {
  const formatter = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const start = startsAt ? formatter.format(new Date(startsAt)) : null;
  const end = endsAt ? formatter.format(new Date(endsAt)) : null;
  if (start && end) {
    return `${start} - ${end} IST`;
  }
  return start ? `${start} IST` : null;
};

const OperationBlockScreen = ({ block, showManageButton = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authenticated = useSelector((state) => state.auth.status === 'authenticated');
  const [remaining, setRemaining] = useState(Number(block?.retryAfterSeconds || 0));
  const windowLabel = useMemo(() => formatWindow(block?.startsAt, block?.endsAt), [block]);

  useEffect(() => {
    setRemaining(Number(block?.retryAfterSeconds || 0));
  }, [block?.retryAfterSeconds, block?.id]);

  useEffect(() => {
    if (!remaining) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setRemaining((current) => Math.max(current - 1, 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [remaining]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(resetContext());
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@parktek.in';
  };

  const handleManageOperationBlocks = () => {
    window.location.assign('/settings/operations');
  };

  const handleStaffLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#F5F7F6',
        display: 'grid',
        placeItems: 'center',
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 560,
          borderRadius: 2,
          border: '1px solid rgba(14,14,14,0.08)',
          p: { xs: 3, sm: 4 },
        }}
      >
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0 }}>
            ParkTek
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {block?.title || 'Scheduled maintenance'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {block?.message || 'ParkTek is temporarily unavailable.'}
          </Typography>
          {windowLabel ? (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {windowLabel}
            </Typography>
          ) : null}
          {remaining > 0 ? (
            <Typography variant="body2" color="text.secondary">
              Retry available in {remaining}s
            </Typography>
          ) : null}
          {block?.supportMessage ? (
            <Typography variant="body2" color="text.secondary">
              {block.supportMessage}
            </Typography>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: '100%' }}>
            {!authenticated ? (
              <Button
                fullWidth
                variant="contained"
                startIcon={<LoginOutlinedIcon />}
                onClick={handleStaffLogin}
              >
                Staff login
              </Button>
            ) : null}
            {showManageButton ? (
              <Button
                fullWidth
                variant="contained"
                startIcon={<SettingsOutlinedIcon />}
                onClick={handleManageOperationBlocks}
              >
                Manage operation blocks
              </Button>
            ) : null}
            <Button
              fullWidth
              variant="contained"
              startIcon={<RefreshOutlinedIcon />}
              onClick={() => dispatch(refreshOperationBlockStatus({ surface: 'WEB_ADMIN' }))}
            >
              Refresh status
            </Button>
            {block?.supportMessage ? (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SupportAgentOutlinedIcon />}
                onClick={handleContactSupport}
              >
                Contact support
              </Button>
            ) : null}
            {authenticated ? (
              <Button fullWidth variant="outlined" startIcon={<LogoutOutlinedIcon />} onClick={handleLogout}>
                Logout
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default OperationBlockScreen;
